import type { FC, PropsWithChildren, ReactElement } from 'react';
import { Children, cloneElement } from 'react';
import Icon from '@components/Icon';
import { Show } from '@components/Show';
import { useEntityStore } from '@ContractBuilder/store';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { useStickyState } from '@hooks/useStickyState';
import clsx from 'clsx';
import { get } from 'lodash-es';
import pluralize from 'pluralize';

import { useSyncDataStore } from '../store';

const Dot = ({ value, className }: { value?: any; className?: string }) => (
  <div
    className={clsx(
      'ml-1 flex w-4 items-center justify-center rounded-sm font-semibold text-white',
      'bg-success-900',
      className,
    )}
    style={{ fontSize: '10px' }}
  >
    {value}
  </div>
);

const jsonClasses = ['rounded-md', 'bg-info-100', 'p-3', 'text-info-900', 'text-wrap'];

interface TabProps {
  id: string;
  isSelected?: boolean;
  render: ReactElement;
  value: any;
  onClick?: () => void;
}

const Tab: FC<TabProps> = ({ id, isSelected, value, onClick }) => (
  <div
    className={clsx('-my-px flex cursor-pointer px-3 py-2', isSelected && 'border-primary-500 border-b-2')}
    onClick={onClick}
  >
    {id} {value && <Dot className={'w-4'} value={value} />}
  </div>
);

enum DebuggerSize {
  default = 'default',
  minimized = 'minimized',
}

const Tabs: FC<PropsWithChildren> = ({ children }) => {
  const first = Children.toArray(children).map((child: any) => child.props.id)[0];
  const [selectedTab, setSelectedTab] = useStickyState(first, 'syncDataDebuggerTabs');
  const selectedChild: any =
    Children.toArray(children).find((c: any) => selectedTab === c?.props?.id) || Children.toArray(children)?.[0];
  const render = selectedChild?.props?.render;
  const isFn = render && render instanceof Function;

  return (
    <div className="flex h-full flex-col text-xs">
      <div className="border-info-500 border-b font-semibold">
        <div className="bg-info-200 flex">
          {Children.toArray(children).map((child: any) => {
            const isSelected = selectedTab === child.props.id;

            return cloneElement(child, {
              ...child.props,
              isSelected,
              key: child.props.id,
              onClick: () => setSelectedTab(child.props.id),
            });
          })}
        </div>
      </div>
      <div className="relative min-h-0 flex-1 overflow-y-auto p-4">{isFn ? render() : render}</div>
    </div>
  );
};

export const SyncDataDebugger: FC = () => {
  const dataItems = useEntityStore((state) => state.submission?.data_items ?? {});
  const { errors, payload } = useSyncDataStore((state) => ({
    errors: state.errors,
    payload: state.payload,
  }));
  const { removedUnusedData, clearUnusedData } = useDatapointsStore((state) => ({
    removedUnusedData: state.removedUnusedData,
    clearUnusedData: state.clearUnusedData,
  }));
  const [size, setSize] = useStickyState(DebuggerSize.default, 'syncDataDebuggerSize');

  const incomingPPLPayload = get(payload, 'incoming', null);
  const outgoingPPLPayload = get(payload, 'outgoing', null);
  const fullDefaultSection = get(payload, 'fullDefaultSection', null);
  const serverRemovedUnusedData = get(payload, 'removedUnusedData', null) as string[] | null;
  const totalRemoved = (removedUnusedData?.length || 0) + (serverRemovedUnusedData?.length || 0);

  return (
    <div
      className={clsx('border-info-500 fixed right-8 bottom-0 z-20 w-[660px] rounded-t border bg-white shadow-lg')}
      style={{ height: size === DebuggerSize.default ? '54vh' : '32px' }}
    >
      <div className="h-full">
        <Tabs>
          <Tab
            id="Sync Errors"
            value={errors ? errors.length : null}
            render={
              <pre className={clsx(jsonClasses)}>
                <Show when={!!errors}>{JSON.stringify(errors, null, 2)}</Show>
                <Show when={!errors}>
                  <p>No errors</p>
                </Show>
              </pre>
            }
          />
          <Tab
            id="CB Payload"
            value={dataItems ? 1 : null}
            render={<pre className={clsx(jsonClasses)}>{JSON.stringify(dataItems, null, 2)}</pre>}
          />
          <Tab
            id="PPL Incoming"
            value={incomingPPLPayload ? 1 : null}
            render={
              <pre className={clsx(jsonClasses)}>
                <Show when={!!incomingPPLPayload}>{JSON.stringify(incomingPPLPayload, null, 2)}</Show>
                <Show when={!incomingPPLPayload}>
                  <p>No payload</p>
                </Show>
              </pre>
            }
          />
          <Tab
            id="Default"
            value={fullDefaultSection ? 1 : null}
            render={
              <pre className={clsx(jsonClasses)}>
                <Show when={!!fullDefaultSection}>{JSON.stringify(fullDefaultSection, null, 2)}</Show>
                <Show when={!fullDefaultSection}>
                  <p>No payload</p>
                </Show>
              </pre>
            }
          />
          <Tab
            id="PPL Outgoing"
            value={outgoingPPLPayload ? 1 : null}
            render={
              <pre className={clsx(jsonClasses)}>
                <Show when={!!outgoingPPLPayload}>{JSON.stringify(outgoingPPLPayload, null, 2)}</Show>
                <Show when={!outgoingPPLPayload}>
                  <p>No payload</p>
                </Show>
              </pre>
            }
          />
          <Tab
            id="Unused Data"
            value={totalRemoved || null}
            render={
              <div className="flex flex-col gap-4">
                <button className="mb-4 self-start rounded-md border p-2" onClick={clearUnusedData}>
                  Clear up unused data
                </button>
                <pre className={clsx(jsonClasses)}>
                  {totalRemoved ? (
                    <div className="flex flex-col gap-4">
                      {serverRemovedUnusedData?.length ? (
                        <div>
                          <p className="mb-2 font-semibold">
                            Server removed {serverRemovedUnusedData.length} unused{' '}
                            {pluralize('field', serverRemovedUnusedData.length)}
                          </p>
                          <ul>
                            {serverRemovedUnusedData.map((path) => (
                              <li key={`srv-${path}`}>- {path}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {removedUnusedData?.length ? (
                        <div>
                          <p className="mb-2 font-semibold">
                            Client removed {removedUnusedData.length} unused{' '}
                            {pluralize('field', removedUnusedData.length)}
                          </p>
                          <ul>
                            {removedUnusedData.map((path) => (
                              <li key={`cli-${path}`}>- {path}</li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <p>No payload yet</p>
                  )}
                </pre>
              </div>
            }
          />
        </Tabs>

        {size === DebuggerSize.default && (
          <Icon
            name="unfold-more"
            className="text-info-800 absolute top-0 right-1 m-3 w-3 cursor-pointer fill-current"
            onClick={() => setSize(DebuggerSize.minimized)}
          />
        )}

        {size === DebuggerSize.minimized && (
          <Icon
            name="unfold-less"
            className="text-info-800 absolute top-0 right-1 m-3 w-3 cursor-pointer fill-current"
            onClick={() => setSize(DebuggerSize.default)}
          />
        )}
      </div>
    </div>
  );
};
